import type { DisplayLabelContext, CertificateFieldUnit } from "../sensors/types";

/** Where to read measurement unit for certificate UI (matches saved sensor / calibration). */
export type CertificateMeasurementUnitSource = {
  /** From parent, e.g. details page */
  sensorUnit?: string | null;
  sensor?: {
    unit?: string | null;
    sensor_type?: { unit?: string | null } | null;
  } | null;
  /** Calibration row / API payload if it includes unit */
  calibration?: { unit?: string | null } | null;
  /** Fallback from CalibrationDisplaySchema */
  schemaDefaultUnit?: string | null;
};

export function buildCertificateDisplayContext(
  sources: CertificateMeasurementUnitSource
): DisplayLabelContext {
  const u =
    sources.sensorUnit?.trim() ||
    sources.sensor?.unit?.trim?.() ||
    sources.sensor?.sensor_type?.unit?.trim?.() ||
    sources.calibration?.unit?.trim?.() ||
    sources.schemaDefaultUnit?.trim() ||
    "MPa";
  return { measurementUnit: u };
}

export function resolveCertificateFieldUnit(
  unit: CertificateFieldUnit | undefined,
  ctx: DisplayLabelContext
): string | undefined {
  if (unit === undefined) return undefined;
  return typeof unit === "function" ? unit(ctx) : unit;
}

/**
 * Convert atmospheric pressure from mmHg (as physically measured and stored on
 * Daily Environmental Data) to the sensor's pressure unit, for the certificate's
 * Atm. Pressure field. 1 mmHg = 0.133322 kPa.
 */
export function convertMmHgToMeasurementUnit(
  mmHg: number,
  measurementUnit: string
): number {
  const kPa = mmHg * 0.133322;
  const u = measurementUnit.trim().toLowerCase();
  return u === "mpa" ? kPa / 1000 : kPa;
}

/** Default atm. pressure string for certificate form; decimals follow unit (no conversion). */
export function formatAtmPressureForCertificateForm(
  value: number | undefined | null,
  measurementUnit: string
): string {
  const u = measurementUnit.trim().toLowerCase();
  if (value !== undefined && value !== null && Number.isFinite(Number(value))) {
    const n = Number(value);
    if (u === "kpa") return n.toFixed(1);
    if (u === "mpa") return n.toFixed(3);
    return n.toFixed(3);
  }
  return u === "kpa" ? "101.325" : "0.100";
}
